import { Component, ElementRef, HostListener, NgZone, OnDestroy, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GuideService } from '../guide.service';
import { HelpTourStep } from '../guide.model';

interface Box { top: number; left: number; width: number; height: number; }

/**
 * The tour: a spotlight on one part of the screen and a card beside it that
 * says what it is, one stop at a time. Stops point at `data-guide` labels on
 * the screen; a stop whose label is not there (a button this role cannot
 * see, a list with nothing in it) is shown as a card in the middle instead,
 * so a tour never stalls on a screen that looks different for this user.
 */
@Component({
    selector: 'spectrum-guide-tour',
    imports: [CommonModule],
    template: `
        @if (guide.tour; as tour) {
            <div class="gt-catch" (click)="end()"></div>
            <div class="gt-spot" [class.none]="!spot" [style.top.px]="spot?.top" [style.left.px]="spot?.left"
                 [style.width.px]="spot?.width" [style.height.px]="spot?.height"></div>
            <div class="gt-card" #card role="dialog" aria-modal="true" [attr.aria-label]="step?.title"
                 [style.top.px]="cardTop" [style.left.px]="cardLeft">
                <div class="gt-k">Step {{ tour.index + 1 }} of {{ tour.steps.length }}</div>
                <h4>{{ step?.title }}</h4>
                <p>{{ step?.body }}</p>
                <div class="gt-foot">
                    <div class="gt-dots" aria-hidden="true">
                        @for (s of tour.steps; track $index) { <i [class.on]="$index === tour.index"></i> }
                    </div>
                    @if (tour.index > 0) {
                        <button type="button" class="gt-btn" (click)="back()">Back</button>
                    } @else {
                        <button type="button" class="gt-btn" (click)="end()">Skip</button>
                    }
                    <button type="button" class="gt-btn primary" #next (click)="forward()">
                        {{ tour.index === tour.steps.length - 1 ? 'Done' : 'Next' }}
                    </button>
                </div>
            </div>
        }
    `,
    styles: [`
        .gt-catch { position: fixed; inset: 0; z-index: 1090; }
        .gt-spot {
            position: fixed; z-index: 1091; pointer-events: none;
            border-radius: 10px;
            box-shadow: 0 0 0 9999px rgba(15, 23, 42, .58);
            outline: 2px solid #fcd34d; outline-offset: 3px;
            transition: top .25s ease, left .25s ease, width .25s ease, height .25s ease;
        }
        .gt-spot.none { top: 50%; left: 50%; width: 0; height: 0; outline: 0; }
        .gt-card {
            position: fixed; z-index: 1092;
            width: 320px; max-width: calc(100vw - 32px);
            background: #fff; border-radius: 14px; padding: 16px;
            box-shadow: 0 20px 44px rgba(15, 23, 42, .3);
            transition: top .25s ease, left .25s ease;
            font-family: 'Inter', system-ui, -apple-system, sans-serif;
            font-size: 13.5px; color: #0f172a; text-align: left; box-sizing: border-box;
        }
        .gt-k { font-size: 11px; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; color: #d97706; }
        h4 { margin: 4px 0 6px; font-size: 15.5px; font-weight: 800; color: #0f172a; }
        p { margin: 0; color: #475569; line-height: 1.55; }
        .gt-foot { display: flex; align-items: center; gap: 8px; margin-top: 14px; }
        .gt-dots { display: flex; gap: 4px; margin-right: auto; flex-wrap: wrap; }
        .gt-dots i { width: 6px; height: 6px; border-radius: 50%; background: #cbd5e1; }
        .gt-dots i.on { width: 16px; border-radius: 999px; background: #d97706; }
        .gt-btn {
            height: 32px; padding: 0 14px; border-radius: 8px; border: 1px solid #e2e8f0; background: #fff;
            font: inherit; font-weight: 700; color: #0f172a; cursor: pointer;
        }
        .gt-btn.primary { background: #4f46e5; border-color: #4f46e5; color: #fff; }
        @media (prefers-reduced-motion: reduce) { .gt-spot, .gt-card { transition: none; } }
    `],
})
export class SpectrumGuideTourComponent implements OnDestroy {
    readonly guide = inject(GuideService);
    private zone = inject(NgZone);

    @ViewChild('card') card?: ElementRef<HTMLElement>;
    @ViewChild('next') nextButton?: ElementRef<HTMLButtonElement>;

    spot: Box | null = null;
    cardTop = 0;
    cardLeft = 0;
    private shownKey = '';
    private frame = 0;

    get step(): HelpTourStep | null {
        const tour = this.guide.tour;
        const step = tour ? tour.steps[tour.index] : null;
        const key = tour ? `${tour.topicId}:${tour.index}` : '';
        if (key !== this.shownKey) {
            this.shownKey = key;
            if (step) {
                this.show(step);
            }
        }
        return step;
    }

    forward(): void {
        const tour = this.guide.tour;
        if (!tour) {
            return;
        }
        if (tour.index >= tour.steps.length - 1) {
            this.end();
        } else {
            tour.index++;
        }
    }

    back(): void {
        const tour = this.guide.tour;
        if (tour && tour.index > 0) {
            tour.index--;
        }
    }

    end(): void {
        this.guide.endTour();
        this.shownKey = '';
    }

    @HostListener('document:keydown', ['$event'])
    onKey(event: KeyboardEvent): void {
        if (!this.guide.tour) {
            return;
        }
        if (event.key === 'Escape') {
            event.stopPropagation();
            this.end();
        } else if (event.key === 'ArrowRight') {
            this.forward();
        } else if (event.key === 'ArrowLeft') {
            this.back();
        }
    }

    @HostListener('window:resize')
    @HostListener('window:scroll')
    onMove(): void {
        if (this.guide.tour && this.step) {
            this.place(this.step, false);
        }
    }

    ngOnDestroy(): void {
        cancelAnimationFrame(this.frame);
    }

    private show(step: HelpTourStep): void {
        const target = this.target(step);
        if (target) {
            target.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
        // Once now, and again after the scroll settles.
        setTimeout(() => this.place(step, true));
        setTimeout(() => this.place(step, false), 380);
    }

    private target(step: HelpTourStep): HTMLElement | null {
        if (!step.anchor) {
            return null;
        }
        const el = document.querySelector<HTMLElement>(`[data-guide="${cssEscape(step.anchor)}"]`);
        if (!el) {
            return null;
        }
        const rect = el.getBoundingClientRect();
        // A label on something hidden or collapsed is as good as missing.
        return rect.width > 0 && rect.height > 0 ? el : null;
    }

    private place(step: HelpTourStep, focus: boolean): void {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.zone.run(() => {
            const card = this.card?.nativeElement;
            const cw = card ? card.offsetWidth : 320;
            const ch = card ? card.offsetHeight : 180;
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const target = this.target(step);
            if (!target) {
                this.spot = null;
                this.cardTop = Math.max(12, (vh - ch) / 2);
                this.cardLeft = Math.max(12, (vw - cw) / 2);
            } else {
                const r = target.getBoundingClientRect();
                const pad = 6;
                this.spot = { top: r.top - pad, left: r.left - pad, width: r.width + pad * 2, height: r.height + pad * 2 };
                const below = r.bottom + 14;
                const above = r.top - ch - 14;
                this.cardTop = below + ch < vh - 8 ? below : above > 8 ? above : Math.max(12, vh - ch - 12);
                this.cardLeft = Math.max(12, Math.min(r.left, vw - cw - 12));
            }
            if (focus) {
                this.nextButton?.nativeElement.focus();
            }
        }));
    }
}

function cssEscape(value: string): string {
    return (window as unknown as { CSS?: { escape(v: string): string } }).CSS?.escape(value) ?? value.replace(/"/g, '\\"');
}
